import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Wrench, Play, CheckCircle, AlertTriangle, RefreshCw, Settings } from 'lucide-react';
import UserTicketStatsComponent from './UserTicketStats';


interface TicketFixResult {
  taskId?: string;
  suggestions?: Array<{
    id: string;
    summary: string;
    diff: string;
    score: number;
  }>;
  status?: string;
  message?: string;
  appliedSuggestion?: any;
}

const TicketFixPanel: React.FC = () => {
  const [ticketId, setTicketId] = useState<string>('');
  const [mode, setMode] = useState<'manual' | 'assist' | 'auto'>('assist');
  const [model, setModel] = useState<string>('soma');
  const [contextFiles, setContextFiles] = useState<string>('');
  const [count, setCount] = useState<number>(3);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TicketFixResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Array<{
    id: string;
    summary: string;
    diff: string;
    score: number;
  }>>([]);
  const [expandedSuggestion, setExpandedSuggestion] = useState<string | null>(null);
  const [autoModeStatus, setAutoModeStatus] = useState<string | null>(null);

  const handleRunTicketFix = async () => {
    if (!ticketId.trim()) {
      setError('Please enter a ticket ID');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResult(null);

      const contextFilesArray = contextFiles
        .split('\n')
        .map(file => file.trim())
        .filter(file => file.length > 0);

      const response = await fetch('/api/automations/tasks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playbook: 'ticket-fix',
          params: {
            ticketId: ticketId.trim(),
            mode,
            model,
            contextFiles: contextFilesArray,
            count
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setResult(data);
      setTaskId(data.taskId);
      
      if (data.suggestions && Array.isArray(data.suggestions)) {
        setSuggestions(data.suggestions);
      }

    } catch (err) {
      console.error('Ticket fix error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      
      const mockSuggestions = mode === 'assist' ? [
        {
          id: 'suggestion_1',
          summary: 'Fix null pointer exception in user authentication',
          diff: '--- a/src/auth.js\n+++ b/src/auth.js\n@@ -15,7 +15,7 @@\n function validateUser(user) {\n-  if (user.email) {\n+  if (user && user.email) {\n     return true;\n   }\n   return false;',
          score: 85
        },
        {
          id: 'suggestion_2', 
          summary: 'Add error handling for missing user data',
          diff: '--- a/src/auth.js\n+++ b/src/auth.js\n@@ -10,6 +10,10 @@\n function authenticateUser(userData) {\n+  if (!userData) {\n+    throw new Error("User data is required");\n+  }\n+\n   const user = parseUserData(userData);',
          score: 78
        },
        {
          id: 'suggestion_3',
          summary: 'Improve input validation with type checking',
          diff: '--- a/src/auth.js\n+++ b/src/auth.js\n@@ -5,6 +5,9 @@\n function parseUserData(data) {\n+  if (typeof data !== "object") {\n+    return null;\n+  }\n   return {\n     email: data.email,\n     name: data.name',
          score: 72
        }
      ] : [];

      const mockResult = {
        taskId: 'mock_task_123',
        status: 'running',
        message: `Started ticket fix workflow for ${ticketId} in ${mode} mode`,
        suggestions: mockSuggestions
      };

      setResult(mockResult);
      setTaskId(mockResult.taskId);
      setSuggestions(mockSuggestions);
      
      if (mode === 'auto' && mockSuggestions.length > 0) {
        const topSuggestion = mockSuggestions[0];
        setAutoModeStatus(`Applying: ${topSuggestion.summary}...`);
        
        setTimeout(() => {
          handleApplySuggestion(topSuggestion.id);
        }, 1500);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApplySuggestion = async (suggestionId: string) => {
    if (!taskId) {
      setError('No active task to continue');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/automations/tasks/${taskId}/continue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          selectionId: suggestionId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResult(data);
      setError(null);
      
      setSuggestions([]);
    } catch (error) {
      console.error('Error applying suggestion:', error);
      setError(error instanceof Error ? error.message : 'Failed to apply suggestion');
      
      const appliedSuggestion = suggestions.find(s => s.id === suggestionId);
      setResult({
        taskId,
        status: 'applied',
        message: `Applied suggestion: ${suggestionId}`,
        appliedSuggestion
      });
      setSuggestions([]);
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewSuggestion = (suggestionId: string) => {
    const suggestion = suggestions.find(s => s.id === suggestionId);
    if (suggestion) {
      setExpandedSuggestion(expandedSuggestion === suggestionId ? null : suggestionId);
    }
  };

  const handleSkipSuggestion = (suggestionId: string) => {
    setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
  };

  const getModeDescription = (selectedMode: string) => {
    switch (selectedMode) {
      case 'manual':
        return 'You will review and approve each step manually';
      case 'assist':
        return 'AI will suggest fixes for you to review and select';
      case 'auto':
        return 'AI will automatically apply the best fix suggestion';
      default:
        return '';
    }
  };

  const getModeColor = (selectedMode: string) => {
    switch (selectedMode) {
      case 'manual':
        return 'text-blue-400';
      case 'assist':
        return 'text-green-400';
      case 'auto':
        return 'text-orange-400';
      default:
        return 'text-gray-400';
    }
  };

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <div className="flex items-center justify-center mb-4">
          <div className="p-3 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl shadow-lg">
            <Wrench className="w-8 h-8 text-white" />
          </div>
        </div>
        <h1 className="text-3xl font-bold cream-gradient-text text-drop-shadow mb-2">Ticket Fix Workflow</h1>
        <p className="text-brand-text-secondary text-lg">Automated ticket resolution with AI assistance</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <UserTicketStatsComponent 
          userId="current-user" 
          className="mb-6"
        />
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-red-900/20 border border-red-500/30 rounded-lg p-4"
        >
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <div>
              <p className="text-red-400 font-medium">Error</p>
              <p className="text-red-300 text-sm mt-1">{error}</p>
              <p className="text-red-300 text-xs mt-2">Showing mock data for demonstration</p>
            </div>
          </div>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6"
      >
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
          <Settings className="w-5 h-5 mr-2 text-green-400" />
          Ticket Fix Configuration
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Ticket ID *
            </label>
            <input
              type="text"
              value={ticketId}
              onChange={(e) => setTicketId(e.target.value)}
              placeholder="Enter ticket ID (e.g., JIRA-123, #456)"
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/30 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Execution Mode
            </label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as 'manual' | 'assist' | 'auto')}
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50"
            >
              <option value="manual">Manual - Full control</option>
              <option value="assist">Assist - AI suggestions with approval</option>
              <option value="auto">Auto - Fully automated</option>
            </select>
            <p className={`text-xs mt-1 ${getModeColor(mode)}`}>
              {getModeDescription(mode)}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              LLM Model
            </label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50"
            >
              <option value="soma">Soma - Advanced reasoning model</option>
              <option value="codellama">CodeLlama - Code-specialized model</option>
              <option value="mixtral">Mixtral - Multi-expert model</option>
              <option value="falcon">Falcon - High-performance model</option>
            </select>
            <p className="text-xs text-slate-400 mt-1">
              Choose the AI model for generating fix suggestions
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Context Files (optional)
            </label>
            <textarea
              value={contextFiles}
              onChange={(e) => setContextFiles(e.target.value)}
              placeholder="Enter file paths, one per line&#10;src/components/Auth.tsx&#10;src/utils/validation.js"
              rows={3}
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/30 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50 resize-none"
            />
            <p className="text-xs text-slate-400 mt-1">
              Provide relevant files to help AI understand the context
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Number of Suggestions
            </label>
            <select
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50"
            >
              <option value={1}>1 suggestion</option>
              <option value={3}>3 suggestions</option>
              <option value={5}>5 suggestions</option>
            </select>
          </div>

          <button
            onClick={handleRunTicketFix}
            disabled={loading || !ticketId.trim()}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all duration-200"
          >
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Running Ticket Fix...</span>
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                <span>Run Ticket Fix</span>
              </>
            )}
          </button>
        </div>
      </motion.div>

      {result && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6"
        >
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-green-400" />
            Ticket Fix Results
          </h2>
          
          <div className="space-y-4">
            <div className="p-4 bg-slate-700/20 border border-slate-600/20 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-300">Task ID</span>
                <span className="text-sm text-white font-mono">{result.taskId}</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-300">Status</span>
                <span className="text-sm text-green-400">{result.status}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-300">Mode</span>
                <span className={`text-sm font-medium ${getModeColor(mode)}`}>{mode}</span>
              </div>
            </div>

            {result.message && (
              <div className="p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                <p className="text-blue-300 text-sm">{result.message}</p>
              </div>
            )}

            {mode === 'assist' && suggestions.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-md font-medium text-white">AI Suggestions ({suggestions.length})</h3>
                {suggestions.map((suggestion, index) => (
                  <div key={suggestion.id} className="p-4 bg-slate-700/30 border border-slate-600/30 rounded-lg">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-medium text-blue-400">#{index + 1}</span>
                          <span className="px-2 py-1 bg-green-900/30 border border-green-500/30 rounded-full text-xs text-green-400">
                            {suggestion.score}% confidence
                          </span>
                        </div>
                        <h4 className="text-white font-medium mb-1">{suggestion.summary}</h4>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mb-3">
                      <button
                        onClick={() => handleApplySuggestion(suggestion.id)}
                        disabled={loading}
                        className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-800 text-white text-sm rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500/50"
                      >
                        Apply
                      </button>
                      <button
                        onClick={() => handlePreviewSuggestion(suggestion.id)}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      >
                        {expandedSuggestion === suggestion.id ? 'Hide' : 'Preview'}
                      </button>
                      <button
                        onClick={() => handleSkipSuggestion(suggestion.id)}
                        className="px-3 py-1.5 bg-slate-600 hover:bg-slate-700 text-white text-sm rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-slate-500/50"
                      >
                        Skip
                      </button>
                    </div>

                    {expandedSuggestion === suggestion.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-3 p-3 bg-slate-800/50 border border-slate-600/30 rounded-lg"
                      >
                        <h5 className="text-sm font-medium text-slate-300 mb-2">Code Changes:</h5>
                        <pre className="text-xs text-slate-300 whitespace-pre-wrap overflow-x-auto bg-slate-900/50 p-3 rounded border border-slate-600/20">
                          {suggestion.diff}
                        </pre>
                      </motion.div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {mode === 'auto' && autoModeStatus && (
              <div className="p-3 bg-orange-900/20 border border-orange-500/30 rounded-lg">
                <div className="flex items-center space-x-2">
                  <RefreshCw className="w-4 h-4 text-orange-400 animate-spin" />
                  <p className="text-orange-300 text-sm">{autoModeStatus}</p>
                </div>
              </div>
            )}

            {result.appliedSuggestion && (
              <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
                <h3 className="text-md font-medium text-green-400 mb-2">Applied Suggestion</h3>
                <p className="text-green-300 text-sm mb-2">{result.appliedSuggestion.summary}</p>
                <pre className="text-xs text-green-300 whitespace-pre-wrap overflow-x-auto bg-green-900/20 p-3 rounded border border-green-600/20">
                  {result.appliedSuggestion.diff}
                </pre>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default TicketFixPanel;
